import type { TimelineBlock } from "../../api/types";
import { LABEL_MIN_PX, type Placed } from "../geometry";
import { theme } from "../theme";
import type { ShowLook, TrackLook } from "./text";

/**
 * How near two placed blocks have to be before the line between them is one boundary on screen
 * rather than two. A hair over the edge stroke's own width, so anything closer than a line is
 * treated as the same line.
 */
const SEAM_TOUCH_PX = 1.5;

/** What a show block wears, and so which of them may merge into one another. */
export function showLook(item: TimelineBlock): ShowLook {
  if (!item.playable) return "dead";
  // A show on air is provisional by definition, so live is checked first: "still running" is
  // the more useful thing to say about it than "not yet reviewed".
  if (item.on_air) return "live";
  return item.state === "provisional" ? "provisional" : "plain";
}

/** The wash a show block is filled with, which is also what fuses it to its neighbour. */
export function showFill(item: TimelineBlock, hoverId: number | null): string {
  switch (showLook(item)) {
    case "dead":
      return theme.tombstoneFill;
    case "live":
      return theme.liveFill;
    case "provisional":
      return theme.provisionalFill;
    default:
      return item.id !== null && item.id === hoverId ? theme.showFillHover : theme.showFill;
  }
}

/** What a track block wears, and so which of them may merge into one another. */
export function trackLook(item: TimelineBlock): TrackLook {
  // Tombstone first: a matched track whose audio is gone is a tombstone like any other, and
  // "there is nothing to play" is the stronger claim.
  if (!item.playable) return "dead";
  return item.matched ? "matched" : "plain";
}

/**
 * The wash a track block is filled with, which is also what fuses it to its neighbour.
 *
 * Null for a matched track at rest, and that is the whole of its look: the show underneath is
 * the block, this is a note written on it, and any wash at all -- however faint -- covers up
 * the thing being annotated. What is left of it on screen is its caption and, under the
 * pointer, the answer below.
 */
export function trackFill(item: TimelineBlock, hoverId: number | null): string | null {
  const hovered = item.id !== null && item.id === hoverId;
  switch (trackLook(item)) {
    case "dead":
      return theme.tombstoneFill;
    case "matched":
      // Lifted to the ordinary track's resting wash under the pointer, not to its hover one:
      // a block has to answer the pointer to read as clickable, and the answer is "this is a
      // track", not "this is now the loudest thing in the lane".
      return hovered ? theme.trackFill : null;
    default:
      return hovered ? theme.trackFillHover : theme.trackFill;
  }
}

/**
 * Whether the boundary in front of `list[i]` is worth a line.
 *
 * Zoomed out far enough, a lane of back-to-back blocks is a picket fence: every block is a
 * sliver of translucent fill and all that is left on screen is the opaque edge at each end of
 * it. The fence says nothing. A boundary delimits two things, and a block too narrow to carry
 * its own name is not a thing anybody can point at yet -- it is part of the run it sits in. So
 * the lines inside a run of slivers are dropped and the run draws as one section with a hard
 * edge at each end, and every line comes back as its blocks widen past `minPx`.
 *
 * Both sides, not either: a boundary with a block wide enough to carry its own name on one side
 * of it is that block's edge, and that block is exactly the thing anybody can point at. Dropped,
 * it takes the sliver beside it off the screen altogether -- no line, no name, nothing -- and
 * fuses the two named blocks either side of that sliver into one section wearing two captions.
 * A run of slivers still collapses; what delimits the run from the blocks it runs between is
 * what survives.
 *
 * Asked once per boundary and answered for both sides at once: a block that dropped an edge its
 * neighbour still drew would leave the line on screen in the neighbour's colour.
 *
 * Only ever within a run of blocks that look alike, and alike is judged per lane. The show band
 * draws four looks and a change between them -- a live show against a finished one, a tombstone
 * against a playable block -- is exactly what an edge is for at any zoom; the jukebox lane
 * draws three, the third being a track the fingerprinter matched inside a show, which is not a
 * review state and must not fuse into the jukebox block beside it. Daylight between two blocks
 * separates them already.
 *
 * `minPx` is the width at which a block earns its own edges, which is also the width at which
 * it earns its name -- one threshold, so a lane can never show a name it has drawn no boundary
 * for. The jukebox lane raises it out of reach past the merge span, which is what collapses the
 * whole lane at once instead of a block at a time.
 */
export function seam(
  list: readonly Placed<TimelineBlock>[],
  i: number,
  lane: "show" | "track",
  minPx = LABEL_MIN_PX,
): boolean {
  const left = list[i - 1];
  const right = list[i];
  // Either end of the lane, or a neighbour culled off screen: nothing to merge into.
  if (!left || !right) return true;
  if (right.x0 - left.x1 > SEAM_TOUCH_PX) return true;
  const alike =
    lane === "show"
      ? showLook(left.item) === showLook(right.item)
      : trackLook(left.item) === trackLook(right.item);
  if (!alike) return true;
  return left.x1 - left.x0 >= minPx || right.x1 - right.x0 >= minPx;
}

/**
 * One fill for every run of blocks `seam` has fused, rather than one per block.
 *
 * A block fill is translucent, and two of them meeting on a fractional pixel each cover part
 * of that column and composite over each other in it. The coverage adds back to one pixel; the
 * alpha does not -- 0.2 over 0.2 across a shared boundary lands at 0.19 -- so the column comes
 * out a shade off the fill either side of it. One block deep that is nothing. Across a lane of
 * three-minute tracks at a day's zoom it is a line every few pixels, which is the picket fence
 * the merge exists to remove, moved out of the edges and into the fill: the lane drew as one
 * section and still arrived striped with one line per track.
 *
 * A run breaks wherever a boundary survived -- the same question, asked with the same `minPx`,
 * so the fill can never fuse across a line that is drawn -- and wherever the wash changes, so
 * the block under the pointer is still its own rect and still lit at the colour it is meant to
 * be rather than at its hover wash composited over the run's. `colour` returns null for a block
 * that is not drawn as a filled rect at all, which ends a run the same way.
 */
export function fillRuns(
  ctx: CanvasRenderingContext2D,
  list: readonly Placed<TimelineBlock>[],
  lane: "show" | "track",
  minPx: number,
  top: number,
  height: number,
  width: number,
  colour: (placed: Placed<TimelineBlock>) => string | null,
): void {
  for (let i = 0; i < list.length;) {
    const paint = colour(list[i]!);
    let j = i + 1;
    if (paint === null) {
      i = j;
      continue;
    }
    while (j < list.length && !seam(list, j, lane, minPx) && colour(list[j]!) === paint) j++;
    // Clipped to the viewport the way a single block is, so a run reaching off both sides is
    // still one rect the width of the canvas rather than one the width of the archive.
    const x0 = Math.max(-2, list[i]!.x0);
    const x1 = Math.min(width + 2, list[j - 1]!.x1);
    ctx.fillStyle = paint;
    ctx.fillRect(x0, top, x1 - x0, height);
    i = j;
  }
}

function strokeVertical(
  ctx: CanvasRenderingContext2D,
  x: number,
  top: number,
  height: number,
): void {
  // Half-pixel offset: a 1.5 px line centred on an integer straddles two device pixels and
  // renders as a soft two-pixel smear.
  const px = Math.round(x) + 0.5;
  ctx.beginPath();
  ctx.moveTo(px, top);
  ctx.lineTo(px, top + height);
  ctx.stroke();
}

export function drawEdges(
  ctx: CanvasRenderingContext2D,
  x0: number,
  x1: number,
  top: number,
  height: number,
  colour: string,
  dashed: boolean,
  /**
   * Which of the two hard edges to draw. Both, unless `seam` judged one of them to be an
   * internal line of a run that is too narrow to be read as separate blocks.
   */
  startEdge = true,
  endEdge = true,
): void {
  ctx.save();
  ctx.strokeStyle = colour;
  ctx.lineWidth = 1.5;
  if (dashed) ctx.setLineDash([4, 3]);
  if (startEdge) strokeVertical(ctx, x0, top, height);
  if (endEdge) strokeVertical(ctx, x1, top, height);
  ctx.setLineDash([]);
  ctx.globalAlpha = 0.45;
  ctx.beginPath();
  ctx.moveTo(x0, top + 0.5);
  ctx.lineTo(x1, top + 0.5);
  ctx.moveTo(x0, top + height - 0.5);
  ctx.lineTo(x1, top + height - 0.5);
  ctx.stroke();
  ctx.restore();
}

export function drawHandles(
  ctx: CanvasRenderingContext2D,
  xs: readonly number[],
  top: number,
  height: number,
  colour: string,
): void {
  ctx.save();
  ctx.fillStyle = colour;
  for (const x of xs) {
    // A stubby bar at each end rather than a full-height one: it reads as something to
    // grab, and it does not compete with the hard edge it sits on.
    ctx.fillRect(Math.round(x) - 2, top + height / 2 - 9, 4, 18);
  }
  ctx.restore();
}
